import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  CheckCircle,
  XCircle,
  Clock,
  Download,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

import Button from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import wizardService from '../../../services/wizardService';
import evaluationService from '../../../services/evaluationService';

const ExecutionResults = ({
  wizardData,
  isExecuting,
  setIsExecuting
}) => {
  const navigate = useNavigate();

  // Execution state
  const [executionData, setExecutionData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState(null);
  const [downloadLink, setDownloadLink] = useState(null);
  const [applyingConfig, setApplyingConfig] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [scheduleConfigured, setScheduleConfigured] = useState(false);

  // Fetch execution results
  const fetchExecutionResults = useCallback(async () => {
    if (!wizardData?.test_data?.run_name) {
      setError('No run name provided');
      setIsExecuting(false);
      return;
    }

    try {
      const response = await wizardService.trackEvaluationExecution(wizardData.test_data.run_name);

      if (response.success) {
        setExecutionData(response.data);
        setProgress(response.data.progress || 0);
        setStatus(response.data.status || 'in_progress');

        // Store evaluation ID for later use
        if (response.data.evaluation_id) {
          // setEvaluationId(response.data.evaluation_id);
        }

        if (response.data.status === 'completed') {
          setIsExecuting(false);
          setDownloadLink(response.data.report_url);

          // Apply pending webhook and schedule configs
          const evalId = response.data.evaluation_id;
          if (evalId) {
            setApplyingConfig(true);
            setConfigStatus('Applying configuration...');

            try {
              const webhookConfig = JSON.parse(sessionStorage.getItem('pendingWebhookConfig') || '{}');
              const scheduleConfig = JSON.parse(sessionStorage.getItem('pendingScheduleConfig') || '{}');

              // Apply webhook if configured
              if (webhookConfig.enabled && webhookConfig.url) {
                try {
                  const webhookResponse = await evaluationService.updateEvaluation(evalId, {
                    webhook_url: webhookConfig.url,
                    webhook_enabled: true
                  });

                  if (webhookResponse.success) {
                    setWebhookConfigured(true);
                    toast.success('Webhook configured successfully');
                  }
                } catch (err) {
                  console.error('Failed to configure webhook:', err);
                  toast.error('Failed to configure webhook');
                }
              }

              // Apply schedule if configured
              if (scheduleConfig.enabled) {
                try {
                  const scheduleResponse = await evaluationService.scheduleEvaluation(evalId, scheduleConfig);

                  if (scheduleResponse.success) {
                    setScheduleConfigured(true);
                    toast.success('Schedule configured successfully');
                  }
                } catch (err) {
                  console.error('Failed to configure schedule:', err);
                  toast.error('Failed to configure schedule');
                }
              }

              // Clear pending configs
              sessionStorage.removeItem('pendingWebhookConfig');
              sessionStorage.removeItem('pendingScheduleConfig');

              setConfigStatus(null);
            } catch (err) {
              console.error('Error applying configs:', err);
              setConfigStatus(null);
            } finally {
              setApplyingConfig(false);
            }
          }
        } else if (response.data.status === 'failed') {
          setIsExecuting(false);
          setError(response.data.error || 'Execution failed');
        }
      } else {
        // Handle API error response
        setError(response.message || 'Failed to fetch execution status');

        if (response.status === 404) {
          setIsExecuting(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch execution results:', err);
      setIsExecuting(false);
      setError(err.message || 'Failed to track execution');
    }
  }, [wizardData, setIsExecuting]);



  // Start polling for results
  useEffect(() => {
    if (!isExecuting) return;

    const pollInterval = setInterval(fetchExecutionResults, 5000);
    return () => clearInterval(pollInterval);
  }, [isExecuting, fetchExecutionResults]);

  // Render execution status
  const renderExecutionStatus = () => {
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center space-x-2 text-yellow-600">
            <Clock className="h-5 w-5" />
            <span>Waiting to start...</span>
          </div>
        );
      case 'in_progress':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-blue-600">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Evaluation in progress...</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        );
      case 'completed':
        return (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span>Evaluation completed successfully</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center space-x-2 text-red-600">
            <XCircle className="h-5 w-5" />
            <span>Evaluation failed: {error}</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Render result details
  const renderResultDetails = () => {
    if (!executionData) return null;

    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Execution Details</CardTitle>
          <CardDescription>Detailed results of the evaluation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <strong>Total Test Cases:</strong> {executionData.total_test_cases || 0}
            </div>
            <div>
              <strong>Passed Test Cases:</strong> {executionData.passed_test_cases || 0}
            </div>
            <div>
              <strong>Failed Test Cases:</strong> {executionData.failed_test_cases || 0}
            </div>
            <div>
              <strong>Execution Time:</strong> {executionData.execution_time || 'N/A'}
            </div>
          </div>

          {downloadLink && (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => window.open(downloadLink, '_blank')}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download Report</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render configuration status
  const renderConfigurationStatus = () => {
    if (status !== 'completed') return null;

    return (
      <Card className="mt-4 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            Configuration Status
          </CardTitle>
          <CardDescription>Applying optional webhook and scheduling settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {applyingConfig && (
              <div className="flex items-center gap-2 text-blue-700">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{configStatus || 'Applying configuration...'}</span>
              </div>
            )}

            {webhookConfigured && (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>Webhook configured successfully</span>
              </div>
            )}

            {scheduleConfigured && (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>Schedule configured successfully</span>
              </div>
            )}

            {!applyingConfig && !webhookConfigured && !scheduleConfigured && (
              <p className="text-sm text-gray-600">
                No additional configuration was applied. You can configure webhooks and scheduling later from the evaluation details page.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-8 space-y-8">
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Evaluation Execution</h2>
        <p className="text-muted-foreground">
          Track the progress and results of your evaluation
        </p>

        {/* Execution Status */}
        <Card>
          <CardHeader>
            <CardTitle>Execution Status</CardTitle>
            <CardDescription>Real-time tracking of your evaluation</CardDescription>
          </CardHeader>
          <CardContent>
            {renderExecutionStatus()}
          </CardContent>
        </Card>

        {/* Result Details */}
        {executionData && renderResultDetails()}

        {/* Configuration Status */}
        {renderConfigurationStatus()}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/evaluations')}
          >
            Back to Evaluations
          </Button>
          {status === 'failed' && (
            <Button
              variant="destructive"
              onClick={() => {
                // Retry logic
                setIsExecuting(true);
                setStatus('pending');
                setError(null);
              }}
            >
              Retry Execution
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionResults;
